import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  useTheme,
  Alert,
  Snackbar,
} from "@mui/material"
import { Link, useLocation } from "react-router-dom"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import useHasVoted from "../../hooks/useHasVoted"
import { useAuthUser } from "react-auth-kit"
import { Event, VotingInstance } from "../../types"
import { EventCard } from "../../components/EventCard"
import api from "../../utils/api"

interface NotificationProps {
  message: string
  onClose: () => void
}

interface InstanceProps {
  instance: VotingInstance
  voterId: string
  counter: number
}

const NotificationSnackbar: React.FC<NotificationProps> = ({
  message,
  onClose,
}) => (
  <Snackbar
    open={!!message}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity="success" sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
)

const VotingInstanceCard: React.FC<InstanceProps> = ({
  instance,
  voterId,
  counter,
}) => {
  const theme = useTheme()
  const hasVoted = useHasVoted(voterId, instance.id)

  return (
    <Card
      sx={{
        m: 2,
        width: "300px",
        border: `1px solid ${theme.palette.primary.main}`,
        padding: "20px",
      }}
    >
      <CardContent sx={{ padding: "0" }}>
        <Typography variant="h6" color="white">
          {`#${counter + 1} ${instance.description}`}
          {instance.status === "open" && (
            <FiberManualRecordIcon
              sx={{ marginLeft: "10px", color: "red", fontSize: "small" }}
            />
          )}
        </Typography>
        <Typography variant="body2" color="white">
          {`Status: ${instance.status}`}
        </Typography>
      </CardContent>
      <RenderActionButton instance={instance} hasVoted={hasVoted} />
    </Card>
  )
}

const RenderActionButton: React.FC<{
  instance: VotingInstance
  hasVoted: boolean | null
}> = ({ instance, hasVoted }) => {
  if (hasVoted === null) {
    return <CircularProgress size={20} />
  }


  console.log("hasVoted", hasVoted)
  console.log("instance.status", instance.status)
  if (hasVoted && instance.status !== "published") {
    return (
      <Typography variant="body2" color="textSecondary">
        You've already voted, please wait for the results.
      </Typography>
    )
  }

  const statusActionMap = {
    open: {
      component: Link,
      to: `/voter/instance/${instance.id}/questions`,
      label: "Join",
    },
    closed: {
      disabled: true,
      label: "Waiting for Admins...",
    },
    published: {
      component: Link,
      to: `/voter/instance/${instance.id}/results`,
      label: "View results",
    },
    "pre-voting": {
      component: Link,
      to: `/voter/instance/${instance.id}/lobby`,
      label: "Join Lobby",
    },
  }

  const action = statusActionMap[instance.status]

  return (
    <Button variant="contained" color="primary" {...action}>
      {action.label}
    </Button>
  )
}

const DisplayContent: React.FC<{
  isLoading: boolean
  votingInstances: VotingInstance[] | null
  loadingError: string | null
  voterId: string
}> = ({ isLoading, votingInstances, loadingError, voterId }) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "32px",
        }}
      >
        <CircularProgress />
      </Box>
    )
  } else if (loadingError) {
    return (
      <Typography variant="body1" m={4} color="error">
        {loadingError}
      </Typography>
    )
  } else if (!votingInstances?.length) {
    // Handle both null and empty array cases here
    return (
      <Typography variant="body1" m={4}>
        No instances available for this event.
      </Typography>
    )
  } else {
    return (
      <>
        <Typography variant="h6" mt={3} mb={0}>
          Select an Instance to Join
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            padding: "32px",
            paddingTop: "0",
          }}
        >
          {votingInstances.map((instance, index) => (
            <VotingInstanceCard
              key={instance.id}
              instance={instance}
              voterId={voterId}
              counter={index}
            />
          ))}
        </Box>
      </>
    )
  }
}

const toTitleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    (txt: string) =>
      txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  )
}

export const VoterDashboardPage: React.FC<{
  notification?: string
}> = ({ notification = "" }) => {
  const [votingInstances, setVotingInstances] = useState<
    VotingInstance[] | null
  >(null)
  const [event, setEvent] = useState<Event | null>(null)
  const location = useLocation()
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(
    notification
  )
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const auth = useAuthUser()
  const voterId = auth()?.id
  const voterName = toTitleCase(auth()?.name ?? "")
  const selectedEventId =
    (location.state as { eventId?: string })?.eventId ?? auth()?.eventId

  useEffect(() => {
    const fetchVotingInstances = async () => {
      if (!selectedEventId) return

      try {
        const { data } = await api.get(
          `/admin/events/${selectedEventId}/voting-instances`
        )
        const sortedInstances = [...data].sort((a: any, b: any) => {
          const aSeconds = a.createdAt?._seconds || 0
          const bSeconds = b.createdAt?._seconds || 0

          return aSeconds - bSeconds
        })

        setVotingInstances(sortedInstances)
      } catch (error) {
        setLoadingError(
          "We're having trouble loading the instances right now. Please try again later."
        )
        setVotingInstances(null)
      }
    }

    const fetchEvent = async () => {
      if (!selectedEventId) return

      try {
        const { data } = await api.get(`/admin/events/${selectedEventId}`)
        setEvent(data)
      } catch (error) {
        console.error(
          `Error fetching data from /admin/events/${selectedEventId}:`,
          error
        )
        setEvent(null)
      }
    }

    fetchVotingInstances()
    fetchEvent()
    const intervalId = setInterval(() => {
      fetchVotingInstances()
    }, 15000)

    return () => clearInterval(intervalId)
  }, [selectedEventId])

  const isLoading = !votingInstances && !loadingError && selectedEventId

  return (
    <>
      <Typography variant="h5" m={3}>
        Welcome {voterName}!
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        {event && <EventCard event={event} />}
      </Box>

      <DisplayContent
        isLoading={isLoading}
        votingInstances={votingInstances}
        loadingError={loadingError}
        voterId={voterId}
      />

      <NotificationSnackbar
        message={snackbarMessage ?? ""}
        onClose={() => setSnackbarMessage(null)}
      />
    </>
  )
}
